import * as Yup from 'yup'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { validateBusinessId, validateSsn } from 'lib/utils/formValidators'

export const schema = (schema) => {
    return Yup.object().shape(schema)
}

export const number = () => {
    return Yup.number()
        .typeError('formElements:validation.notNumber')
}

export const positiveNumber = () => {
    return number()
        .positive('formElements:validation.notPositiveNumber')
}

export const positivePrice = () => {
    return positiveNumber()
}

export const integer = () => {
    return Yup.number()
        .typeError('formElements:validation.notNumber')
        .max(999999999, 'formElements:validation.formatError')
        .integer('formElements:validation.notInteger')
}

export const positiveInteger = () => {
    return integer()
        .positive('formElements:validation.notPositiveNumber')
}

export const countryId = () => {
    return integer()
        .typeError('formElements:validation.requiredCountry')
        .positive('formElements:validation.notPositiveNumber')
}

export const registerNumber = () => {
    return Yup.string()
        .test('length', 'formElements:validation.invalidRegNumber', (value) => {
            return !value || value === '' || value.length >= 3
        })
        .max(20, 'formElements:validation.tooLong')
}

export const name = () => {
    return Yup.string()
        .max(30, 'formElements:validation.tooLong')
}

export const vehicleName = () => {
    return name()
}

export const email = () => {
    return Yup.string()
        .email('formElements:validation.invalidEmail')
        .test('emailChars', 'formElements:validation.invalidEmailCharacters', (value) => !value || /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value))
        .max(255, 'formElements:validation.tooLong')
}

export const newPassword = () => {
    return Yup.string()
        .test('passwordStrength', 'formElements:password.validationError', value => !value || /((?=.*[a-z])(?=.*[A-Z])(?=.*[\d\s!"#$%&\\'()*+,-.\\/:;<=>?@[\]^_`{|}~]).{8,32})$/u.test(value))
}

export const password = () => {
    return Yup.string()
}

export const shortTitle = () => {
    return Yup.string()
        .max(50, 'formElements:validation.tooLong')
}

export const message = (length = 2000) => {
    return Yup.string()
        .max(length, 'formElements:validation.tooLong')
}

export const ssn = (ssnAllowException = false) => {
    return Yup.string()
        .test('ssn', 'formElements:validation.invalidSsn', (value) => {
            return (!value || value === '') || validateSsn(value, ssnAllowException)
        })
}

export const ssnNotSameAs = (ssnAllowException = false, buyerSsnFull, sellerSsnFull) => {
    return Yup.mixed()
        .test('ssn', 'formElements:validation.invalidSsn', (value) => {
            return (!value || value === '') || validateSsn(value, ssnAllowException)
        })
        .test('sameSsn', 'formElements:validation.invalidSsnSameAsSeller', value => {
            if (value) {
                return buyerSsnFull !== sellerSsnFull
            }
            return true
        })
}

export const businessId = () => {
    return Yup.string()
        .test('businessId', 'formElements:validation.invalidBusinessId', (value) => {
            return (!value || value === '') || validateBusinessId(value)
        })
}

export const phoneNumber = () => {
    return Yup.string()
        .max(50, 'formElements:validation.tooLong')
        .test('phoneNumber', 'formElements:validation.invalidPhoneNumber', value => {
            let isValid = true
            if (value) {
                const parsedPhoneNumber = parsePhoneNumberFromString(value, 'FI')
                isValid = parsedPhoneNumber ? parsedPhoneNumber.isValid() : false
            }
            return isValid
        })
}

export const address = () => {
    return Yup.string()
        .min(3, 'formElements:validation.tooShort')
        .max(255, 'formElements:validation.tooLong')
}

export const trafiDigitalCertificate = () => {
    return Yup.string()
        .min(6, 'formElements:validation.tooShort')
        .max(6, 'formElements:validation.tooLong')
}

/**
 * This is conditional validation with more strict rules if country is Finland.
 */
export const zipWithCountry = (finlandCountryId, countryFieldName) => {
    return Yup.string()
        .when(countryFieldName, {
            is: value => parseInt(value) === parseInt(finlandCountryId),
            then: Yup.string().matches(/^[0-9]{5}$/, 'formElements:global.validationZipError'),
            otherwise: Yup.string().matches(/^[0-9]*$/, 'formElements:global.validationZipError')
        })
}

export const city = () => {
    return Yup.string()
        .min(2, 'formElements:validation.tooShort')
        .max(30, 'formElements:validation.tooLong')
}

export const priceInteger = () => {
    return positiveInteger()
        .max(9999999, 'formElements:validation.formatError')
}

export const zip = () => {
    return Yup.string()
        .min(5, 'formElements:global.validationZipError')
}

export const date = () => {
    return Yup.string() // @TODO: bug in formik, has to be string for now. https://github.com/jaredpalmer/formik/issues/1945
}

export const boolean = () => {
    return Yup.boolean()
}

export const booleanTrue = () => {
    return Yup.boolean()
        .oneOf([true], 'formElements:validation.required')
}

export const acceptTerms = () => {
    return Yup.boolean()
        .oneOf([true], 'formElements:validation.acceptTerms')
}
